import * as React from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type PasswordRule = {
  label: string;
  test: (value: string) => boolean;
};

export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  { label: "At least 8 characters", test: (v) => v.length >= 8 },
  { label: "An uppercase letter (A–Z)", test: (v) => /[A-Z]/.test(v) },
  { label: "A lowercase letter (a–z)", test: (v) => /[a-z]/.test(v) },
  { label: "A number (0–9)", test: (v) => /\d/.test(v) },
  {
    label: "A special character (!@#$…)",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

export function passwordMeetsRules(value: string, rules = DEFAULT_PASSWORD_RULES): boolean {
  return rules.every((r) => r.test(value));
}

type PasswordInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className, ...props }, ref) {
    const [visible, setVisible] = React.useState(false);
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          className={cn("pr-10", className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    );
  },
);

export function PasswordRequirements({
  value,
  rules = DEFAULT_PASSWORD_RULES,
  className,
}: {
  value: string;
  rules?: PasswordRule[];
  className?: string;
}) {
  return (
    <ul className={cn("space-y-1 text-xs", className)}>
      {rules.map((rule) => {
        const ok = rule.test(value);
        return (
          <li
            key={rule.label}
            className={cn(
              "flex items-center gap-1.5",
              ok ? "text-success" : "text-muted-foreground",
            )}
          >
            {ok ? (
              <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} />
            ) : (
              <X className="w-3.5 h-3.5 shrink-0" />
            )}
            <span>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}
